import { useTranslation } from "react-i18next";
import { products, type Product } from "@/data/products";

export type TranslatedProduct = Omit<Product, "shortDescription" | "description" | "features" | "specs"> & {
  shortDescription: string;
  description: string;
  features: string[];
  specs?: { label: string; value: string }[];
};

export function useTranslatedProducts() {
  const { t, i18n } = useTranslation();

  const getTranslatedProduct = (product: Product): TranslatedProduct => {
    const key = `productData.${product.slug}`;
    const hasTranslation = i18n.exists(`${key}.shortDescription`);

    if (!hasTranslation) return product;

    return {
      ...product,
      shortDescription: t(`${key}.shortDescription`),
      description: t(`${key}.description`),
      features: t(`${key}.features`, { returnObjects: true }) as string[],
      specs: i18n.exists(`${key}.specs`)
        ? (t(`${key}.specs`, { returnObjects: true }) as { label: string; value: string }[])
        : product.specs,
    };
  };

  const translatedProducts = products.map(getTranslatedProduct);

  const getTranslatedProductBySlug = (slug: string) => {
    const product = products.find((p) => p.slug === slug);
    if (!product) return undefined;
    return getTranslatedProduct(product);
  };

  return { translatedProducts, getTranslatedProductBySlug };
}
